import { forwardRef, useCallback, useState } from 'react'

import { Input } from '~/ui'
import { cn } from '~/utils/classnames'

/**
 * Sanitizes a currency input string:
 * - Replaces commas with dots (for locale compatibility)
 * - Removes any non-numeric characters except dots
 * - Handles multiple dots by keeping only the first one
 * - Returns null if the result is not a valid number
 */
export function sanitizeCurrencyValue(value: string): string | null {
  if (!value || value.trim() === '') {
    return null
  }

  // Replace commas with dots (handle European format)
  let sanitized = value.replace(/,/g, '.')

  // Remove any character that isn't a digit or dot
  sanitized = sanitized.replace(/[^\d.]/g, '')

  // Handle multiple dots - keep only the first one
  const parts = sanitized.split('.')
  if (parts.length > 2) {
    sanitized = parts[0] + '.' + parts.slice(1).join('')
  }

  // Remove leading dots
  sanitized = sanitized.replace(/^\./, '')

  // Check if it's a valid number
  if (sanitized === '' || isNaN(parseFloat(sanitized))) {
    return null
  }

  return sanitized
}

/**
 * Parses a currency input value to a number (in the display unit, e.g., euros)
 * Returns null if invalid
 */
export function parseCurrencyValue(value: string): number | null {
  const sanitized = sanitizeCurrencyValue(value)
  if (sanitized === null) {
    return null
  }
  return parseFloat(sanitized)
}

/**
 * Formats a number for display in a currency input
 */
export function formatCurrencyValue(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  if (typeof value === 'string') {
    const parsed = parseCurrencyValue(value)
    if (parsed === null) return ''
    return parsed.toString()
  }

  if (value === 0) {
    return ''
  }

  return value.toString()
}

export interface CurrencyInputProps extends Omit<
  React.ComponentPropsWithRef<'input'>,
  'onChange' | 'value' | 'size'
> {
  value?: number | string
  onChange?: (value: string) => void
  onValueChange?: (value: number | null) => void
  onValidationError?: (error: string | null) => void
  currencySymbol?: string
  invalid?: boolean
  allowNegative?: boolean
  min?: number
  max?: number
  size?: 'sm' | 'md' | 'lg'
}

/**
 * CurrencyInput component with sanitize-on-blur behavior
 *
 * Features:
 * - Allows free typing (doesn't fight the user)
 * - Sanitizes on blur: replaces commas with dots, removes invalid characters
 * - Shows the sanitized value so users see what will be saved
 * - Calls onValidationError if the value is invalid after sanitization
 */
export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      value,
      onChange,
      onValueChange,
      onValidationError,
      currencySymbol = '€',
      invalid,
      allowNegative = false,
      min,
      max,
      size,
      className,
      onBlur,
      ...props
    },
    ref
  ) => {
    // Track internal value for controlled input
    const [internalValue, setInternalValue] = useState(() =>
      formatCurrencyValue(value)
    )

    // Determine display value - use internal state if typing, otherwise use prop
    const displayValue =
      typeof value === 'string' ? value : formatCurrencyValue(value)

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setInternalValue(newValue)
        onChange?.(newValue)
      },
      [onChange]
    )

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const rawValue = e.target.value

        // Sanitize the value
        const sanitized = sanitizeCurrencyValue(rawValue)

        if (rawValue && sanitized === null) {
          // Invalid input that couldn't be sanitized
          onValidationError?.('Invalid number format')
          onChange?.('')
          onValueChange?.(null)
          setInternalValue('')
        } else if (sanitized !== null) {
          const numericValue = parseFloat(sanitized)

          // Validate min/max
          let error: string | null = null
          if (!allowNegative && numericValue < 0) {
            error = 'Value cannot be negative'
          } else if (min !== undefined && numericValue < min) {
            error = `Value must be at least ${min}`
          } else if (max !== undefined && numericValue > max) {
            error = `Value cannot exceed ${max}`
          }

          onValidationError?.(error)

          // Update with sanitized value
          onChange?.(sanitized)
          onValueChange?.(numericValue)
          setInternalValue(sanitized)
        } else {
          // Empty value
          onValidationError?.(null)
          onChange?.('')
          onValueChange?.(null)
          setInternalValue('')
        }

        // Call original onBlur if provided
        onBlur?.(e)
      },
      [
        onChange,
        onValueChange,
        onValidationError,
        onBlur,
        allowNegative,
        min,
        max,
      ]
    )

    return (
      <Input.Group size={size}>
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={invalid}
          className={cn(className)}
          {...props}
        />
        <Input.Suffix className="text-foreground/48 font-normal">
          {currencySymbol}
        </Input.Suffix>
      </Input.Group>
    )
  }
)

CurrencyInput.displayName = 'CurrencyInput'

/**
 * Sanitizes a numeric input string:
 * - Replaces commas with dots (for locale compatibility)
 * - Removes any non-numeric characters except dots and minus sign
 * - Handles multiple dots by keeping only the first one
 * - Returns null if the result is not a valid number
 */
export function sanitizeNumericValue(
  value: string,
  options: { allowDecimal?: boolean; allowNegative?: boolean } = {}
): string | null {
  const { allowDecimal = true, allowNegative = false } = options

  if (!value || value.trim() === '') {
    return null
  }

  // Replace commas with dots (handle European format)
  let sanitized = value.replace(/,/g, '.')

  // Remove any character that isn't a digit, dot, or minus sign
  if (allowNegative) {
    sanitized = sanitized.replace(/[^\d.-]/g, '')
    // Keep minus sign only at the beginning
    const hasLeadingMinus = sanitized.startsWith('-')
    sanitized = sanitized.replace(/-/g, '')
    if (hasLeadingMinus) {
      sanitized = '-' + sanitized
    }
  } else {
    sanitized = sanitized.replace(/[^\d.]/g, '')
  }

  if (allowDecimal) {
    // Handle multiple dots - keep only the first one
    const parts = sanitized.split('.')
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('')
    }
  } else {
    // Remove all dots for integer-only
    sanitized = sanitized.replace(/\./g, '')
  }

  // Remove leading dots (but preserve leading minus)
  if (sanitized.startsWith('.')) {
    sanitized = sanitized.slice(1)
  } else if (sanitized.startsWith('-.')) {
    sanitized = '-' + sanitized.slice(2)
  }

  // Check if it's a valid number
  if (sanitized === '' || sanitized === '-' || isNaN(parseFloat(sanitized))) {
    return null
  }

  return sanitized
}

/**
 * Parses a numeric input value to a number
 * Returns null if invalid
 */
export function parseNumericValue(
  value: string,
  options: { allowDecimal?: boolean; allowNegative?: boolean } = {}
): number | null {
  const sanitized = sanitizeNumericValue(value, options)
  if (sanitized === null) {
    return null
  }
  return parseFloat(sanitized)
}

/**
 * Formats a number for display in a numeric input
 */
export function formatNumericValue(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  if (typeof value === 'string') {
    const parsed = parseNumericValue(value)
    if (parsed === null) return ''
    return parsed.toString()
  }

  if (value === 0) {
    return ''
  }

  return value.toString()
}

export interface NumberInputProps extends Omit<
  React.ComponentPropsWithRef<'input'>,
  'onChange' | 'value' | 'size'
> {
  value?: number | string
  onChange?: (value: string) => void
  onValueChange?: (value: number | null) => void
  onValidationError?: (error: string | null) => void
  invalid?: boolean
  allowNegative?: boolean
  allowDecimal?: boolean
  min?: number
  max?: number
  suffix?: string
  size?: 'sm' | 'md' | 'lg'
}

/**
 * NumberInput component with sanitize-on-blur behavior
 *
 * Features:
 * - Allows free typing (doesn't fight the user)
 * - Sanitizes on blur: replaces commas with dots, removes invalid characters
 * - Shows the sanitized value so users see what will be saved
 * - Calls onValidationError if the value is invalid after sanitization
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      onValueChange,
      onValidationError,
      invalid,
      allowNegative = false,
      allowDecimal = true,
      min,
      max,
      suffix,
      size,
      className,
      onBlur,
      ...props
    },
    ref
  ) => {
    // Track internal value for controlled input
    const [internalValue, setInternalValue] = useState(() =>
      formatNumericValue(value)
    )

    // Determine display value - use internal state if typing, otherwise use prop
    const displayValue =
      typeof value === 'string' ? value : formatNumericValue(value)

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setInternalValue(newValue)
        onChange?.(newValue)
      },
      [onChange]
    )

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const rawValue = e.target.value

        // Sanitize the value
        const sanitized = sanitizeNumericValue(rawValue, {
          allowDecimal,
          allowNegative,
        })

        if (rawValue && sanitized === null) {
          // Invalid input that couldn't be sanitized
          onValidationError?.('Invalid number format')
          onChange?.('')
          onValueChange?.(null)
          setInternalValue('')
        } else if (sanitized !== null) {
          const numericValue = parseFloat(sanitized)

          // Validate min/max
          let error: string | null = null
          if (!allowNegative && numericValue < 0) {
            error = 'Value cannot be negative'
          } else if (min !== undefined && numericValue < min) {
            error = `Value must be at least ${min}`
          } else if (max !== undefined && numericValue > max) {
            error = `Value cannot exceed ${max}`
          }

          onValidationError?.(error)

          // Update with sanitized value
          onChange?.(sanitized)
          onValueChange?.(numericValue)
          setInternalValue(sanitized)
        } else {
          // Empty value
          onValidationError?.(null)
          onChange?.('')
          onValueChange?.(null)
          setInternalValue('')
        }

        // Call original onBlur if provided
        onBlur?.(e)
      },
      [
        onChange,
        onValueChange,
        onValidationError,
        onBlur,
        allowNegative,
        allowDecimal,
        min,
        max,
      ]
    )

    if (suffix) {
      return (
        <Input.Group size={size}>
          <Input
            ref={ref}
            type="text"
            inputMode={allowDecimal ? 'decimal' : 'numeric'}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={invalid}
            className={cn(className)}
            {...props}
          />
          <Input.Suffix className="text-foreground/48 font-normal">
            {suffix}
          </Input.Suffix>
        </Input.Group>
      )
    }

    return (
      <Input
        ref={ref}
        type="text"
        inputMode={allowDecimal ? 'decimal' : 'numeric'}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={invalid}
        size={size}
        className={cn(className)}
        {...props}
      />
    )
  }
)

NumberInput.displayName = 'NumberInput'
